import { observable, runInAction } from "mobx"

import { Model } from "@/lib/Model.ts"

import type { AiAgentTaskDomElement, TID } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IObservableValue } from "mobx"


interface ImageState {
  objectUrl: string | null
  loading: boolean
  error: string | null
}

export class DomElementScreenshotModel extends Model {
  private cleanup?: () => void

  readonly imageState: IObservableValue<ImageState>

  constructor(private readonly queries: Queries, private readonly taskId: string, private readonly element: AiAgentTaskDomElement) {
    super("DomElementScreenshotModel")

    this.imageState = observable.box<ImageState>({
      objectUrl: null,
      loading: false,
      error: null
    })

    void this.loadScreenshot()

    this.lifetime.add(() => {
      if (this.cleanup) {
        this.cleanup()
      }
    }, "DomElementScreenshotModel:cleanup")
  }

  private readonly loadScreenshot = async () => {
    const screenshotBlobId = this.element.screenshotBlobId

    if (!screenshotBlobId) return

    runInAction(() => {
      this.imageState.set({
        objectUrl: null,
        loading: true,
        error: null
      })
    })

    try {
      const objectUrl = await this.queries.apiClient.downloadDomElementScreenshot(
        this.taskId as TID,
        screenshotBlobId
      )

      if (objectUrl) {
        this.cleanup = () => URL.revokeObjectURL(objectUrl)

        runInAction(() => {
          this.imageState.set({
            objectUrl: objectUrl,
            loading: false,
            error: null
          })
        })
      }
    } catch (error) {
      runInAction(() => {
        this.imageState.set({
          objectUrl: null,
          loading: false,
          error: error instanceof Error ? error.message : "Failed to load screenshot"
        })
      })
    }
  }
}